import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import { businessSchema, SITE_URL, DEFAULT_OG_IMAGE } from "../lib/seoSchemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function buildArticleSchema(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${SITE_URL}/blog/${post.slug}`,
    headline: post.title,
    description: post.excerpt || "",
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: {
      "@type": "Person",
      name: "Caleb R. Holder, DBA",
      url: `${SITE_URL}/about`,
    },
    publisher: { "@id": `${SITE_URL}#business` },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${post.slug}`,
    },
  };
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => {
        if (!cancelled) setPost(res.data);
      })
      .catch((err) => {
        if (!cancelled) setNotFound(err.response?.status === 404);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="py-32 flex items-center justify-center text-ink-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading
      </div>
    );
  }
  if (notFound) return <Navigate to="/blog" replace />;
  if (!post) return null;

  const metaDescription =
    (post.excerpt || "").slice(0, 300) ||
    `${post.title}: Holder Hospitality Consulting field notes.`;

  return (
    <article data-testid="page-blog-post">
      <Seo
        title={`${post.title} | Holder Hospitality Consulting`}
        description={metaDescription}
        image={DEFAULT_OG_IMAGE}
        type="article"
        schemas={[businessSchema, buildArticleSchema(post)]}
      />

      <section className="bg-white py-16 lg:py-24 border-b border-ink-line">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-brand-700 hover:text-brand-800 transition-colors mb-10"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all posts
          </Link>
          <div className="eyebrow mb-6">Field Notes</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-700 leading-[1.05] tracking-tight">
            {post.title}
          </h1>
          <div className="mt-6 flex items-center gap-2 text-sm text-ink-muted">
            <Calendar className="w-4 h-4 text-brand-400" />
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20" data-testid="blog-post-body">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div
            className="prose-blog"
            dangerouslySetInnerHTML={{ __html: post.body }}
          />
        </div>
      </section>

      <section className="bg-brand-100 py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-brand-700 text-sm font-medium border-b border-brand-700 pb-1 hover:border-brand-400 transition-colors"
            data-testid="blog-back-link-bottom"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all posts
          </Link>
        </div>
      </section>
    </article>
  );
}
